import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gauge, HardDrive, Loader2 } from "lucide-react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Input } from "@/components/ui/input";
import { PageHero } from "@/components/layout/PageHero";
import { AlertTriangle, CAPACITY_EVENT, useCapacity } from "@/components/capacity/useCapacity";
import { alertLabel } from "@/components/capacity/CapacityBanner";
import { PushToggle } from "@/components/capacity/PushToggle";
import { DownloadRegulatorCard } from "@/components/capacity/DownloadRegulatorCard";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";

const METRICS = ["storage", "memory", "cpu", "disk", "queue"];
const COLORS = { storage: "#f5c542", memory: "#60a5fa", cpu: "#f87171", disk: "#a3e635", queue: "#c084fc" };
const BAR = { green: "bg-emerald-500", orange: "bg-amber-500", red: "bg-red-600" };

const GaugeCard = ({ k, m }) => {
  const { t, fmtBytes } = useI18n();
  const host = k === "disk" && m.host ? ` · ${t("cap.hostDisk", { pct: m.host.pct.toLocaleString(document.documentElement.lang) })}` : "";
  const detail = m.total ? `${fmtBytes(m.used)} / ${fmtBytes(m.total)}${host}` : k === "queue" ? t("cap.queueDetail", { peak: m.peak_waiting, active: m.active, max: m.max_active }) : k === "cpu" ? t("cap.cpuDetail", { n: m.cores }) : "";
  return (
    <li className="card-carbon min-w-0 rounded-2xl p-4" data-testid={`capacity-gauge-${k}`}>
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-400">
        {m.level !== "green" && <AlertTriangle level={m.level} className="h-4 w-4" />} {t(`cap.m.${k}`)}
      </p>
      <p className="mt-2 font-display text-3xl font-bold text-white" data-testid={`capacity-value-${k}`}>{Math.round(m.value)} %</p>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-neutral-800" role="meter" aria-valuenow={m.value} aria-valuemin={0} aria-valuemax={100}>
        <div className={`h-full ${BAR[m.level]} transition-[width] duration-500`} style={{ width: `${Math.min(100, m.value)}%` }} />
      </div>
      {detail && <p className="mt-2 break-words text-xs text-neutral-500">{detail}</p>}
    </li>
  );
};

const StorageSetting = ({ value }) => {
  const { t } = useI18n();
  const [gb, setGb] = useState(value);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/capacity/settings", { storage_gb: Number(gb) });
      toast.success(t("verify.saved"));
      window.dispatchEvent(new Event(CAPACITY_EVENT));
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };
  return (
    <form onSubmit={save} className="card-carbon flex flex-wrap items-end gap-3 self-start rounded-2xl p-5" data-testid="capacity-storage-form">
      <label className="min-w-0 flex-1 text-xs text-neutral-400"><span className="flex items-center gap-2"><HardDrive className="h-4 w-4 text-[var(--gold)]" /> {t("cap.storageCap")}</span>
        <Input inputMode="decimal" value={gb} onChange={(e) => setGb(e.target.value)} className="mt-1 bg-black/50" data-testid="capacity-storage-input" />
      </label>
      <button type="submit" className="btn-gold btn-sm" data-testid="capacity-storage-save">{t("cap.save")}</button>
    </form>
  );
};

const History = () => {
  const { t, fmtDate } = useI18n();
  const [h, setH] = useState(null);
  useEffect(() => {
    api.get("/admin/capacity/history").then(({ data }) => setH(data));
  }, []);
  if (!h) return null;
  return (
    <section className="card-carbon min-w-0 rounded-2xl p-5" data-testid="capacity-history">
      <h2 className="font-display text-sm font-bold uppercase tracking-wider text-white">{t("cap.history")}</h2>
      <div className="mt-4 h-64 w-full" dir="ltr">
        <ResponsiveContainer>
          <LineChart data={h.points}>
            <CartesianGrid stroke="#262626" />
            <XAxis dataKey="ts" tickFormatter={(v) => new Date(v).toLocaleDateString(undefined, { day: "2-digit", month: "2-digit" })} stroke="#737373" fontSize={11} />
            <YAxis domain={[0, 100]} stroke="#737373" fontSize={11} width={32} />
            <Tooltip contentStyle={{ background: "#0b0b0c", border: "1px solid #404040" }} labelFormatter={(v) => fmtDate(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {METRICS.map((m) => <Line key={m} type="monotone" dataKey={m} name={t(`cap.m.${m}`)} stroke={COLORS[m]} dot={false} strokeWidth={2} />)}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <h3 className="mt-6 text-xs font-semibold uppercase tracking-wider text-neutral-400">{t("cap.alertLog")}</h3>
      {!h.alerts.length && <p className="mt-2 text-sm text-neutral-500" data-testid="capacity-alert-log-empty">{t("cap.noAlerts")}</p>}
      <ul className="mt-2 space-y-1" data-testid="capacity-alert-log">
        {h.alerts.map((a, i) => (
          <li key={i} className="flex min-w-0 flex-wrap items-center gap-2 text-xs text-neutral-300">
            <AlertTriangle level={a.level} className="h-4 w-4" /> <span className="min-w-0 break-words">{alertLabel(t, a)}</span>
            <span className="text-neutral-500">· {fmtDate(a.created_at)} · {t(a.email ? "cap.emailOk" : "cap.emailKo")} · {t("cap.pushCount", { n: a.push })}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

const StorageGains = ({ b }) => {
  const { t, locale } = useI18n();
  const gb = (n) => new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(n / 1024 ** 3);
  const pct = (n) => new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(n);
  return (
    <section className="card-carbon grid gap-2 rounded-2xl p-5 text-sm sm:grid-cols-2" data-testid="capacity-storage-gains">
      <p className="text-white" data-testid="capacity-compression-gain">{t("cap.compGain", { gb: gb(b.comp_saved), pct: pct(b.comp_pct) })}
        <span className="block text-xs text-neutral-500">{t("cap.compCount", { n: b.compressed })}</span></p>
      <p className="text-white" data-testid="capacity-dedup-gain">{t("cap.dedupGain", { gb: gb(b.dedup_saved) })}
        <span className="block text-xs text-neutral-500">{t("cap.blobCount", { n: b.blobs })}</span></p>
    </section>
  );
};

export default function AdminCapacity() {
  const { t } = useI18n();
  const [data] = useCapacity(30000);
  return (
    <div data-testid="admin-capacity-page">
      <PageHero eyebrow={t("admin.eyebrow")} title={t("cap.title")}>
        <p className="mt-4 max-w-2xl text-sm text-neutral-400">{t("cap.intro")}</p>
      </PageHero>
      <div className="mx-auto max-w-6xl space-y-6 px-4 pb-16 sm:px-8">
        {!data ? <Loader2 className="h-6 w-6 animate-spin text-[var(--gold)]" /> : (
          <>
            <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="capacity-gauges">
              {METRICS.map((k) => <GaugeCard key={k} k={k} m={data.metrics[k]} />)}
            </ul>
            <p className="flex items-center gap-2 text-xs text-neutral-500"><Gauge className="h-4 w-4" /> {t("cap.levels", { s: data.interval })}</p>
            {data.blobs && <StorageGains b={data.blobs} />}
            <DownloadRegulatorCard />
            {data.queue_hint?.suggest_next && (
              <p className="rounded-xl border border-[rgba(245,197,66,0.35)] bg-black/40 p-3 text-sm font-semibold text-[var(--gold-bright)]" data-testid="capacity-queue-hint">
                {t("tier.saturation", { pct: Math.round(data.queue_hint.full_pct) })}
              </p>
            )}
            <div className="grid gap-4 lg:grid-cols-2">
              <StorageSetting key={data.storage_gb} value={data.storage_gb} />
              <PushToggle />
            </div>
          </>
        )}
        <History />
      </div>
    </div>
  );
}
